import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPage } from '@/shared/components/error-page';
import type { AppRuntime } from './runtime';

interface Props {
  runtime: AppRuntime;
  children?: ReactNode;
}

interface State {
  error: unknown;
  failed: boolean;
}

/**
 * Last-resort boundary around everything below the theme: reports the crash and shows the plain
 * error page. Route errors are handled earlier by the root route's error component.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined, failed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.runtime.errorReporter.report('boundary', error, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <ErrorPage
          error={this.state.error}
          onRetry={() => {
            this.setState({ error: undefined, failed: false });
          }}
        />
      );
    }
    return this.props.children;
  }
}
